import { HTMLAttributes, forwardRef, ReactNode } from 'react';
import ReactSelect, { CSSObjectWithLabel, Props, StylesConfig } from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import classNames from 'classnames';
import { ExplainerText } from 'components/molecules/Input';
import { GroupedSelectOption, SelectOption } from 'utils/frontendTypes';
import Typography from 'components/atoms/Typography';

type BaseStyles = Omit<CSSObjectWithLabel, 'accentColor'>;
const customStyles: Partial<StylesConfig> = {
  dropdownIndicator: (provided: BaseStyles) => ({
    ...provided,
    color: '#808191',
    fontSize: 14,
    marginLeft: 0,
    padding: 6,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  control: (provided: BaseStyles) => ({
    ...provided,
    borderColor: 'transparent',
    '&:focus-within': { borderColor: '#6590A8' },
    boxShadow: 'none',
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 12,
    paddingRight: 12,
    minHeight: 0,
    backgroundColor: 'transparent',
    borderRadius: 'inherit',
    cursor: 'pointer',
  }),
  menu: (provided: BaseStyles) => ({ ...provided, zIndex: '99999 !important' }),
  menuPortal: (provided: BaseStyles) => ({ ...provided, zIndex: 99999 }),
  singleValue: (provided: BaseStyles) => ({ ...provided, fontSize: 14, color: '#262524', fontWeight: 500 }),
  valueContainer: (provided: BaseStyles) => ({ ...provided, paddingLeft: 0, paddingRight: 0 }),
  option: (provided: BaseStyles, state) => ({
    ...provided,
    color: '#262524',
    backgroundColor: state.isFocused ? '#E4E4E4 !important' : '#fff',
    '&:hover': { cursor: 'pointer' },
  }),
  multiValueLabel: (provided: BaseStyles) => ({ ...provided, color: '#262524' }),
  placeholder: (provided: BaseStyles) => ({
    ...provided,
    fontFamily: 'Open Sans, sans-serif',
    fontSize: 14,
    color: '#808191',
    fontWeight: 400,
  }),
};

export interface SelectProps extends Props {
  additional?: any;
  cacheOptions?: boolean;
  defaultOptions?: boolean;
  disabled?: boolean;
  error?: string;
  explainerText?: ReactNode;
  explainerTextAbove?: ReactNode;
  explainerToggleLabel?: ReactNode;
  isAsync?: boolean;
  isCreateable?: boolean;
  isSearchable?: boolean;
  label?: ReactNode;
  loadOptions?: (...args: any[]) => any;
  onChange?: any;
  onCreateOption?: (inputValue: string) => void;
  options?: SelectOption[] | GroupedSelectOption[];
  overrideStyles?: Partial<StylesConfig>;
  required?: boolean;
  value?: SelectOption | SelectOption[];
  wrapperProps?: HTMLAttributes<HTMLDivElement>;
  dataDurableId: string;
  blurInputOnSelect?: boolean;
}

const Select = forwardRef((props: SelectProps, ref: any) => {
  const className = classNames(
    'appearance-none rounded-sm w-full text-glooCharcoal leading-tight text-sm border border-charcoal-300 bg-white',
    { 'opacity-50': props.disabled },
    props.className,
  );
  const styles: Partial<StylesConfig> = { ...customStyles, ...props.overrideStyles };

  const { dataDurableId, ...rest } = props;

  return (
    <div {...props.wrapperProps} className="inline-block relative w-full">
      {props.label && (
        <Typography variant="label" className="pl-1 pb-2">
          {props.label}
          {props.required && <span className="text-red text-xs">*</span>}
        </Typography>
      )}
      {props.explainerTextAbove && (
        <ExplainerText text={props.explainerTextAbove} toggleLabel={props.explainerToggleLabel} isAbove />
      )}

      {props.isCreateable ? (
        <CreatableSelect
          isDisabled={props.disabled}
          {...rest}
          className={className}
          styles={styles}
          ref={ref}
          data-durable-id={dataDurableId}
          data-intercom-target={dataDurableId}
        />
      ) : props.isAsync ? (
        <AsyncSelect
          menuPortalTarget={document.querySelector('body')}
          isDisabled={props.disabled}
          {...rest}
          loadOptions={props.loadOptions as any}
          className={className}
          styles={styles}
          ref={ref}
          data-durable-id={dataDurableId}
          data-intercom-target={dataDurableId}
        />
      ) : (
        <ReactSelect
          isDisabled={props.isDisabled}
          isSearchable={props.isSearchable}
          menuPortalTarget={document.querySelector('body')}
          {...rest}
          className={className}
          styles={styles}
          ref={ref}
          data-durable-id={dataDurableId}
          data-intercom-target={dataDurableId}
          blurInputOnSelect={props.blurInputOnSelect}
        />
      )}
      {props.error ? <div className="text-xs text-red">{props.error}</div> : null}
      {props.explainerText && <ExplainerText text={props.explainerText} toggleLabel={props.explainerToggleLabel} />}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
